import { useState } from 'react';
import UnreadBadge from './UnreadBadge';
import PropTypes from 'prop-types';
import { useI18n } from '../context/I18nContext';
import { useLogin } from '../context/LoginContext';
import { useContactList } from '../hooks/useContactList';
import FancyTimeStamp from './FancyTimeStamp';
import { isValidAvatar } from '../utils/avatar';
import { MESSAGE_TYPES } from '../constants';
import './ConversationList.css';

const toUnixSeconds = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  return value > 1e12 ? Math.floor(value / 1000) : value;
};

const ConversationListItem = ({
  title = 'Placeholder Title',
  avatars = [],
  lastMessage = null,
  isSelected = false,
  onClick = () => {},
  contacts = [],
  isInvite = false,
  unreadCount = 0,
  isEncrypted = false,
}) => {
  const { t } = useI18n();
  const [isHovered, setIsHovered] = useState(false);
  const { currentUser } = useLogin();


  const getUserName = (id) => {
    if (id === currentUser?.id) return 'You';
    if (!contacts?.length) return id;
    const user = contacts.find(u => u.id === id);
    if (user) return user.name;
    return id;
  };

  const highlightState = (isSelected)
    ? 'selected'
    : ((isHovered) ? 'hovered' : '');

  const senderId = lastMessage?.senderId ?? lastMessage?.sender ?? '';
  const senderName = senderId
    ? getUserName(senderId)
    : lastMessage?.senderName || lastMessage?.sender || '';
  const rawBody = typeof lastMessage === 'string'
    ? lastMessage
    : lastMessage?.body ?? lastMessage?.content ?? '';

  // Check if this is an attachment type
  const isAttachment = lastMessage?.type && [
    MESSAGE_TYPES.IMAGE,
    MESSAGE_TYPES.VIDEO,
    MESSAGE_TYPES.AUDIO,
    MESSAGE_TYPES.FILE,
  ].includes(lastMessage.type);

  let senderPrefix = '';
  let messageContent = '';

  if (typeof lastMessage === 'string') {
    messageContent = lastMessage;
  } else if (rawBody) {
    if (senderName) {
      /**
       * Message prefix logic based on event type:
       * - Membership events already contain complete sentences (e.g., "Alice joined the room")
       * - Profile changes need name with space (e.g., "Alice changed their profile picture")
       * - Regular messages need name with colon (e.g., "Alice: Hello world")
       */
      const isMembership = lastMessage?.type === MESSAGE_TYPES.MEMBERSHIP;
      const isProfileChange = lastMessage?.type === MESSAGE_TYPES.PROFILE_CHANGE;

      if (isMembership) {
        senderPrefix = '';
      } else if (isProfileChange) {
        senderPrefix = `${senderName} `;
      } else {
        senderPrefix = `${senderName}: `;
      }

      messageContent = rawBody;
    } else {
      messageContent = rawBody;
    }
  } else if (senderName) {
    messageContent = senderName;
  }

  const hasContent = Boolean(senderPrefix || messageContent);
  const shouldShowLastMessage = hasContent;
  const timestampSecondsRaw = toUnixSeconds(lastMessage?.timestamp ?? null);
  const hasTimestamp = typeof timestampSecondsRaw === 'number' && timestampSecondsRaw > 0;

  // Get initial letter for placeholder
  const placeholderInitial = title?.charAt(0)?.toUpperCase() || '?';

  return (
    <div
      className={`conversation-list-item ${highlightState}`}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="conversation-list-item-icons">
        {isEncrypted && (
          <ion-icon className="conversation-list-item-icon" name="lock-closed" title="Encrypted" />
        )}
      </div>
      <div className="conversation-list-item-avatars">
        {
          avatars.length === 1 && isValidAvatar(avatars[0]) ? (
            <img className="conversation-list-item-avatar-single" src={avatars[0]} alt="Avatar" crossOrigin="anonymous" />
          ) : avatars.length >= 2 && isValidAvatar(avatars[0]) && isValidAvatar(avatars[1]) ? (
            <>
              <img className="conversation-list-item-avatar-first" src={avatars[0]} alt="Avatar" crossOrigin="anonymous" />
              <img className="conversation-list-item-avatar-second" src={avatars[1]} alt="Avatar" crossOrigin="anonymous" />
            </>
          ) : avatars.length >= 2 && (isValidAvatar(avatars[0]) || isValidAvatar(avatars[1])) ? (
            // Partial placeholders for dual avatar case
            <>
              {isValidAvatar(avatars[0]) ? (
                <img className="conversation-list-item-avatar-first" src={avatars[0]} alt="Avatar" crossOrigin="anonymous" />
              ) : (
                <div className="conversation-list-item-avatar-placeholder-first">{placeholderInitial}</div>
              )}
              {isValidAvatar(avatars[1]) ? (
                <img className="conversation-list-item-avatar-second" src={avatars[1]} alt="Avatar" crossOrigin="anonymous" />
              ) : (
                <div className="conversation-list-item-avatar-placeholder-second">{placeholderInitial}</div>
              )}
            </>
          ) : (
            // No valid avatars - show single placeholder
            <div className="conversation-list-item-avatar-placeholder">{placeholderInitial}</div>
          )
        }
      </div>
      <div className="conversation-list-item-info" title={title}>
        <div className="conversation-list-item-title">
          {title}
        </div>
        {isInvite && <span className="conversation-list-item-invite-badge">{t('ContainerListView.invited')}</span>}
        {
          hasTimestamp && !isInvite && (
            <div className="conversation-list-item-timestamp">
              <FancyTimeStamp unixTime={timestampSecondsRaw} short showNow />
            </div>
          )
        }
      </div>
      {
        shouldShowLastMessage && (
          <div className="conversation-list-item-message">
            <span className="conversation-list-item-message-text">
              {senderPrefix && <span>{senderPrefix}</span>}
              {isAttachment && <i className="fa fa-paperclip" />}
              {messageContent && <span className={(isAttachment) ? 'conversation-list-item-message-attachment' : ''}>{messageContent}</span>}
              {lastMessage?.caption && <div>{lastMessage.caption}</div>}
            </span>
            <span className="conversation-list-item-unread-badge-wrapper">
              <UnreadBadge count={unreadCount} />
            </span>
          </div>
        )
      }
    </div>
  );
};

ConversationListItem.propTypes = {
  title: PropTypes.string,
  avatars: PropTypes.arrayOf(PropTypes.string),
  lastMessage: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      type: PropTypes.string,
      senderId: PropTypes.string,
      sender: PropTypes.string,
      senderName: PropTypes.string,
      body: PropTypes.string,
      content: PropTypes.string,
      timestamp: PropTypes.number,
    }),
  ]),
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
  contacts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
  })),
  isInvite: PropTypes.bool,
  unreadCount: PropTypes.number,
  isEncrypted: PropTypes.bool,
};

const ConversationList = ({
  conversations = [],
  selectedConversationId = '',
  onSelectConversation,
  onItemClick, // Support both prop names for compatibility
  isLoading: _isLoading = false,
}) => {
  const { contactList } = useContactList();
  // Use whichever callback is provided
  const handleSelect = onItemClick || onSelectConversation || (() => {});
  return (
    <div className="conversation-list">
      {
        conversations.map(conversation => {
          const fallbackTimestamp = toUnixSeconds(conversation.lastActivity ?? null);
          const rawLastMessage = conversation.lastMessage ?? null;
          const normalizedLastMessage = typeof rawLastMessage === 'string'
            ? {
              body: rawLastMessage,
              content: rawLastMessage,
              senderId: '',
              senderName: '',
            }
            : rawLastMessage;

          const messageTimestamp = normalizedLastMessage?.timestamp != null
            ? toUnixSeconds(normalizedLastMessage.timestamp)
            : fallbackTimestamp;

          const normalizedTimestamp = (typeof messageTimestamp === 'number' && messageTimestamp > 0)
            ? messageTimestamp
            : null;

          const lastMessage = normalizedLastMessage
            ? {
              ...normalizedLastMessage,
              senderId: normalizedLastMessage.senderId ?? normalizedLastMessage.sender ?? '',
              senderName: normalizedLastMessage.senderName ?? '',
              timestamp: normalizedTimestamp,
            }
            : null;
          return (
            <ConversationListItem
              key={conversation.id}
              title={conversation.name}
              avatars={conversation.avatars || []}
              lastMessage={lastMessage}
              isSelected={selectedConversationId === conversation.id}
              onClick={() => handleSelect(conversation.id)}
              contacts={contactList}
              isInvite={conversation.isInvite || false}
              unreadCount={conversation.unreadCount}
              isEncrypted={conversation.isEncrypted || false}
            />
          );
        })
      }
    </div>
  );
};

ConversationList.propTypes = {
  conversations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    avatars: PropTypes.arrayOf(PropTypes.string),
    messages: PropTypes.array,
    lastMessage: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        senderId: PropTypes.string,
        sender: PropTypes.string,
        senderName: PropTypes.string,
        body: PropTypes.string,
        content: PropTypes.string,
        timestamp: PropTypes.number,
      }),
    ]),
    isInvite: PropTypes.bool,
  })),
  selectedConversationId: PropTypes.string,
  onSelectConversation: PropTypes.func,
  onItemClick: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default ConversationList;
